/* HOME — community landing at /hub.
 *
 * WHAT IS HAPPENING IN THE PROPHETS COMMUNITY RIGHT NOW?
 *
 * Reveal contract:
 *   HubBootLoader → STABLE, correctly-aligned Hub shell. The loader
 *   only protects the user from an unresolved auth/layout shell; it
 *   does NOT wait for Home data. Once auth session is resolved and
 *   the sidebar geometry is guaranteed pre-paint, we reveal the
 *   shell. Team News / Worldwide / Presence / Transmissions /
 *   Leaderboard then hydrate progressively inside that stable
 *   layout using their own skeleton states.
 *
 * Layout-race prevention (see the detailed comment on the
 * `useLayoutEffect` at the top of the component): the sidebar body
 * class is applied BEFORE `.hub-root` ever enters the DOM, so the
 * CSS rule that offsets Hub content by the sidebar width is
 * matching from the first paint — no 0 → 232 px transition ever
 * animates on reveal.
 */
import { useEffect, useLayoutEffect, useState, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HubHeader, hub, AuthGate, HUB_AUTH_GATE_STATES, PostCard } from "./HubPage";
import HubOnboarding from "@/components/hub/onboarding/HubOnboarding";
import { HUB_ONBOARDING_OPEN_EVENT } from "@/lib/hubOnboardingSeen";
import { getSocket } from "../lib/socket";
import { TeamNewsSection } from "../components/hub/TeamNewsSection";
import { LeaderboardSection } from "../components/hub/LeaderboardSection";
import { OnlinePresencePanel } from "../components/hub/OnlinePresencePanel";
// v14.17.26 — code-split the globe. `ProphetsWorldwide` pulls in
// world-atlas topojson (~250 KB gz) + d3-geo + the canvas render
// loop. Loading it lazily means Home shell + Team News + Leaderboard
// paint immediately even on cold visits; the globe hydrates from the
// separate bundle after the initial paint, with a lightweight
// placeholder in its slot so layout does not jump.
const ProphetsWorldwide = lazy(() =>
  import("../components/hub/ProphetsWorldwide"));
import HubBootLoader from "../components/hub/HubBootLoader";
import "./HubPage.css";
import "./HubHomePage.css";

/* Auth resolution safety cap. `/hub/me` is normally very fast (<100
   ms), but under a network hiccup we don't want to hang the loader
   forever — after this window we reveal the shell anyway with an
   unauthenticated `me` fallback (the sidebar's user pill flips to
   the identity chip as soon as `/hub/me` finally resolves). This is
   NOT a minimum loader duration — the loader disappears the moment
   auth resolves, however quick that is. */
const AUTH_RESOLUTION_CAP_MS = 3000;

/* Ensure the `hub-sb-active` (and optional `hub-sb-active--collapsed`)
   body class is on <body> BEFORE any Hub content is rendered.
   Without this, the CSS rule
     `body.hub-sb-active .hub-root { padding-left: var(--hub-sb-w);
                                     transition: padding-left 200ms }`
   only starts matching after HubHeader's own useLayoutEffect adds
   the class — which fires AFTER `.hub-root` is first inserted into
   the DOM (since HubHeader is a child of `.hub-root`). Result: the
   root paints once with padding-left 0, then the class flips on and
   a 200ms transition animates 0 → 232 px, producing the visible
   "content underneath the sidebar" jump on hard refresh.
   Applying the class in HubHomePage's own useLayoutEffect — which
   runs while the boot loader is still on screen — guarantees the
   class is present the moment `.hub-root` first mounts, so the
   first paint is already at padding-left 232 px and no transition
   ever triggers on reveal. */
function useSidebarBodyClassEarly() {
  useLayoutEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.add("hub-sb-active");
    try {
      const collapsed = window.localStorage.getItem("hub.sidebar.collapsed") === "1";
      document.body.classList.toggle("hub-sb-active--collapsed", collapsed);
    } catch { /* localStorage unavailable → expanded default is fine */ }
    // No cleanup here on purpose. HubHeader owns the class lifecycle
    // (add on mount + remove on unmount) so navigating away from any
    // Hub page still tears the class down. This hook is a one-shot
    // "apply before .hub-root paints" — the paired remove happens
    // when HubHeader unmounts inside HubHomePage's unmount commit.
  }, []);
}

/* Adds `hub-sb-ready` on <body> AFTER the very first paint of the
   Hub shell (`.hub-root`). See the CSS comment on
   `body.hub-sb-active.hub-sb-ready .hub-root` for the full rationale:
   the transition rule for `padding-left` is gated on this class so
   that the first paint has NO transition property in scope, killing
   the browser quirk that otherwise animates 0 → 232 px on shell
   reveal. */
function useReadyClassAfterShellPaint(revealed) {
  useLayoutEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (!revealed) return undefined;
    // Double-rAF: the first rAF fires at the end of the current
    // frame (right after `.hub-root`'s initial paint); the second
    // rAF fires at the start of the next frame — guaranteeing we
    // are past first paint before adding the class. Adding the
    // class earlier can re-introduce the transition on the very
    // first `padding-left` computation.
    let r1 = 0, r2 = 0;
    r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => {
        document.body.classList.add("hub-sb-ready");
      });
    });
    return () => {
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
    };
  }, [revealed]);
}

/* ── Latest Transmissions — real data ─────────────────────────── */
function LatestTransmissionsSection() {
  const [state, setState] = useState({ loading: true, err: null, items: [] });
  useEffect(() => {
    let cancelled = false;
    hub.get("/hub/feed?sort=newest&limit=4").then(({ data }) => {
      if (cancelled) return;
      setState({ loading: false, err: null, items: data.items || [] });
    }).catch(e => {
      if (cancelled) return;
      setState({ loading: false,
                 err: e?.response?.data?.detail || "Could not load transmissions.",
                 items: [] });
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="hh-section" data-testid="hh-transmissions">
      <header className="hh-section-head">
        <h2 className="hh-section-title">LATEST TRANSMISSIONS</h2>
        <Link to="/hub/transmissions"
              className="hh-section-cta"
              data-testid="hh-transmissions-view-all">
          VIEW ALL <span aria-hidden="true">→</span>
        </Link>
      </header>
      {state.loading ? (
        <div className="hh-tx-grid" data-testid="hh-transmissions-loading">
          {[0, 1, 2, 3].map(k => (
            <div key={k} className="hh-tx-card hh-tx-skel" />
          ))}
        </div>
      ) : state.err ? (
        <div className="hh-empty" data-testid="hh-transmissions-err">
          {state.err}
        </div>
      ) : state.items.length === 0 ? (
        <div className="hh-empty" data-testid="hh-transmissions-empty">
          No transmissions yet. Be the first to broadcast.
        </div>
      ) : (
        <div className="hh-tx-grid" data-testid="hh-transmissions-grid">
          {state.items.slice(0, 4).map(item => (
            <div key={item.submission_id} className="hh-tx-slot"
                 data-testid={`hh-tx-${item.submission_id}`}>
              <PostCard item={item} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ── Page ─────────────────────────────────────────────────────── */
export default function HubHomePage() {
  const navigate = useNavigate();

  // Apply `hub-sb-active` on <body> synchronously BEFORE any Hub
  // content mounts. See the hook comment for the full rationale —
  // this is the fix for the "content briefly underneath the sidebar"
  // race on hard refresh.
  useSidebarBodyClassEarly();

  /* Prehydrated Home state. Each entry is either:
       • undefined  → not resolved by reveal time (the child renders
         its own loading skeleton and fetches on its own),
       • null       → resolved OK but no payload (e.g. no team news),
       • object     → resolved with a payload.
     Children read these as `initial` props and — if defined — seed
     their state directly, skipping their first network call. */
  const [me, setMe]                = useState(undefined);
  const [teamNews, setTeamNews]    = useState(undefined);
  const [presence, setPresence]    = useState(undefined);
  const [wwPoints, setWwPoints]    = useState(undefined);
  const [wwSummary, setWwSummary]  = useState(undefined);
  const [revealed, setRevealed]    = useState(false);

  // Add `hub-sb-ready` on <body> AFTER the shell has painted once,
  // which enables the padding-left/max-width transitions for future
  // collapse toggles without them firing on the very first paint.
  // Must be called AFTER `revealed` is declared above.
  useReadyClassAfterShellPaint(revealed);

  const [authGate, setAuthGate] = useState(() => {
    if (typeof window === "undefined") return null;
    const s = new URLSearchParams(window.location.search).get("auth") || "";
    return HUB_AUTH_GATE_STATES[s] ? s : null;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const s = new URLSearchParams(window.location.search).get("auth");
    if (s && (HUB_AUTH_GATE_STATES[s] || s === "ok" || s === "error")) {
      const url = new URL(window.location.href);
      url.searchParams.delete("auth");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // Home prehydration + reveal gate.
  //
  // Reveal timing: as soon as `/hub/me` resolves (or the auth safety
  // cap fires). We do NOT block reveal on the four Home-content
  // endpoints — they still fire in parallel to opportunistically
  // seed the Home children (skipping their first fetch when we have
  // a payload by reveal time), but any child that didn't get seeded
  // renders its own skeleton and fetches independently inside the
  // stable shell. This matches the user directive to "not wait for
  // all Home content" while still delivering a populated first
  // paint whenever the network cooperates.
  useEffect(() => {
    let cancelled = false;

    hub.get("/hub/me")
      .then(r => { if (!cancelled) { setMe(r.data); setRevealed(true); } })
      .catch(() => { if (!cancelled) { setMe({ authenticated: false }); setRevealed(true); } });

    hub.get("/hub/team-news/current")
      .then(r => { if (!cancelled) setTeamNews(r.data?.news || null); })
      .catch(() => { if (!cancelled) setTeamNews(null); });

    hub.get("/hub/presence/online")
      .then(r => {
        if (cancelled) return;
        setPresence({ count: r.data?.count || 0, users: r.data?.users || [] });
      })
      .catch(() => { if (!cancelled) setPresence({ count: 0, users: [] }); });

    hub.get("/hub/worldwide/points")
      .then(r => {
        if (cancelled) return;
        setWwPoints(Array.isArray(r.data?.points) ? r.data.points : []);
      })
      .catch(() => { if (!cancelled) setWwPoints([]); });

    hub.get("/hub/worldwide/summary")
      .then(r => { if (!cancelled) setWwSummary(r.data?.summary || null); })
      .catch(() => { if (!cancelled) setWwSummary(null); });

    // Auth resolution safety cap — only fires when /hub/me is
    // pathologically slow, so we never hang the shell forever.
    const capTimer = setTimeout(() => {
      if (cancelled) return;
      setRevealed(true);
    }, AUTH_RESOLUTION_CAP_MS);

    return () => { cancelled = true; clearTimeout(capTimer); };
  }, []);

  // Open the shared Hub WebSocket so presence dots stay live. Safe
  // to call before reveal — it's a singleton and the presence panel
  // will pick up updates on its own polling/socket loop once mounted.
  useEffect(() => { getSocket(); }, []);

  const logout = async () => {
    await hub.post("/hub/auth/logout").catch(() => null);
    navigate("/hub");
  };

  // Gate reveal on `revealed`. Until then, only the Prophets loader.
  // Because `useSidebarBodyClassEarly` has already put `hub-sb-active`
  // on <body>, the sidebar layout offset is present from the moment
  // `.hub-root` first mounts below — the reveal is layout-stable.
  if (!revealed) {
    return <HubBootLoader />;
  }
  const meResolved = me !== undefined ? me : { authenticated: false };

  return (
    <div className="hub-root" data-testid="hub-home-page">
      {/* First-visit animated onboarding tutorial. Portal-mounted; the
          "HOW IT WORKS" button below dispatches an event to reopen it
          on demand. Not mounted at the App/router layer, so the
          public landing page never renders it. */}
      <HubOnboarding />
      <HubHeader me={meResolved} active="home" onLogout={logout} />
      <div className="hub-shell hh-shell">
        <header className="hh-hero" data-testid="hh-hero">
          <div className="hh-hero-titles">
            <h1 className="hh-hero-title">HOME</h1>
            <p className="hh-hero-sub">
              What is happening in the Prophets community right now.
            </p>
            {/* HOW IT WORKS — visible whether authenticated or not.
                Always reopens the tutorial from scene 1, ignoring the
                localStorage seen flag (spec). */}
            <button type="button"
                    className="hh-how-it-works"
                    onClick={() => window.dispatchEvent(
                      new CustomEvent(HUB_ONBOARDING_OPEN_EVENT))}
                    data-testid="hub-how-it-works-btn">
              <span aria-hidden="true">?</span> HOW IT WORKS
            </button>
          </div>
          <OnlinePresencePanel initial={presence} />
        </header>

        {/* A. TEAM NEWS — live publisher (single current item) */}
        <TeamNewsSection initial={teamNews} />

        {/* B. PROPHETS WORLDWIDE — anonymous globe of connected users.
            Code-split via `React.lazy` so its ~250 KB world-atlas
            payload + canvas init never blocks Home paint. A tiny
            skeleton reserves the layout slot so the sidebar +
            Team News + Leaderboard remain scrollable and interactive
            while the globe chunk streams in. */}
        <Suspense fallback={
          <section className="pw-section" data-testid="pw-lazy-skeleton"
                   aria-hidden="true"
                   style={{ minHeight: 460 }} />
        }>
          <ProphetsWorldwide
            initialPoints={wwPoints}
            initialSummary={wwSummary}
          />
        </Suspense>

        {/* C. LATEST TRANSMISSIONS — real data */}
        <LatestTransmissionsSection />

        {/* D. LEADERBOARD — live TOP-5 */}
        <LeaderboardSection />
      </div>
      {authGate && <AuthGate state={authGate} onClose={() => setAuthGate(null)} />}
    </div>
  );
}
